// Household Information — the housing and residence facts of one benefit application.
//
// Editable through `PATCH /api/benefitapplications/{id}/household-information`. Household
// size, the five age brackets, and the disabled count are deliberately absent: the server
// derives them from the member collection on every write, so offering them as inputs would
// invite staff to enter a number the next save silently overwrites.
import { useEffect } from "react"
import { useBenefitDraftState, useBenefitReviewActive, useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import { IconHome } from "@tabler/icons-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "sonner"
import { EditableCardActions, Field, FieldGrid } from "@/components/benefit/field"
import {
  familyTypeLabels,
  housingStatusLabels,
  housingTypeLabels,
  incorporationLabels,
  selectOptions,
} from "@/lib/benefit-enums"
import { boolLabel, enumLabel, fmtMoney } from "@/lib/benefit-format"
import { householdEnergyFacts } from "@/lib/household-summary"
import { saveBenefitChange } from "@/lib/benefit-save"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import type { BenefitDetailDto } from "@/lib/benefit-types"
import { api } from "@/lib/api"
import {
  householdInformationDraft,
  householdInformationRequest,
  incorporationByte,
} from "@/lib/household"

const housingStatusOptions = selectOptions(housingStatusLabels)
const housingTypeOptions = selectOptions(housingTypeLabels)
const incorporationOptions = selectOptions(incorporationLabels)

export function HouseholdInformationSection({
  benefit,
  canEdit,
  onUpdated,
}: {
  benefit: BenefitDetailDto
  canEdit: boolean
  onUpdated: (updated: BenefitDetailDto) => void
}) {
  const [isEditing, setIsEditing, editToken] = useBenefitEdit(benefit.concurrencyToken, "housing")
  const [pending, setIsSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const isSaving = pending || reviewing
  const [draft, setDraft] = useBenefitDraftState("housing", "draft", () => householdInformationDraft(benefit))

  // A reload after a conflict replaces the benefit underneath a closed card; the form has
  // to follow it rather than keep showing what the previous read said.
  useEffect(() => {
    if (!isEditing) setDraft(householdInformationDraft(benefit))
  }, [benefit, isEditing])

  const beginEdit = () => {
    if (!canEdit) return
    setDraft(householdInformationDraft(benefit))
    setIsEditing(true)
  }

  const cancelEdit = () => {
    if (!isSaving) setIsEditing(false)
  }

  const save = async () => {
    if (!isEditing || isSaving || !canEdit) return
    const { error, request } = householdInformationRequest(draft, editToken)
    if (error || !request) {
      toast.error(error ?? "The household information could not be saved.")
      return
    }

    setIsSaving(true)
    const { saved, reloaded } = await saveBenefitChange(
      benefit.id,
      onUpdated,
      () =>
        api.patch<BenefitDetailDto>(
          `/api/benefitapplications/${benefit.id}/household-information`,
          request,
        ),
      "Household information updated.",
    )
    setIsSaving(false)
    if (saved || reloaded) setIsEditing(false)
  }

  // Owned by other blocks of the application; shown here because staff read them together.
  const energy = householdEnergyFacts(benefit)

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconHome className="size-5" />
            <div>
              <CardTitle className="text-base">Household Information</CardTitle>
              <CardDescription>Housing and residence facts captured at intake</CardDescription>
            </div>
          </div>
          <EditableCardActions
            isEditing={isEditing}
            isSaving={isSaving}
            canEdit={canEdit}
            saveDisabled={!canEdit}
            restorePanelFocus
            onEdit={beginEdit}
            onSave={save}
            onCancel={cancelEdit}
          />
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="housing-status">Housing Status</Label>
              <Select
                value={draft.housingStatus}
                disabled={isSaving}
                onValueChange={(housingStatus) => setDraft((d) => ({ ...d, housingStatus }))}
              >
                <SelectTrigger id="housing-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {housingStatusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="housing-type">Housing Type</Label>
              <Select
                value={draft.housingType}
                disabled={isSaving}
                onValueChange={(housingType) => setDraft((d) => ({ ...d, housingType }))}
              >
                <SelectTrigger id="housing-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {housingTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="housing-cost">Monthly Rent/Mortgage Payment</Label>
              <Input
                id="housing-cost"
                inputMode="decimal"
                value={draft.housingCost}
                disabled={isSaving}
                onChange={(event) => setDraft((d) => ({ ...d, housingCost: event.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lived-years">Years at Residence</Label>
              <Input
                id="lived-years"
                inputMode="numeric"
                value={draft.livedAtResidenceYears}
                disabled={isSaving}
                onChange={(event) =>
                  setDraft((d) => ({ ...d, livedAtResidenceYears: event.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lived-months">Additional Months at Residence</Label>
              <Input
                id="lived-months"
                inputMode="numeric"
                value={draft.livedAtResidenceMonths}
                disabled={isSaving}
                onChange={(event) =>
                  setDraft((d) => ({ ...d, livedAtResidenceMonths: event.target.value }))
                }
              />
            </div>
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <p className="font-medium">Family Composition</p>
              <p className="mt-1 text-muted-foreground">Derived from the selected members, relationships, ages, and application date.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="incorporated">Incorporated Area</Label>
              <Select
                value={draft.incorporated}
                disabled={isSaving}
                onValueChange={(incorporated) => setDraft((d) => ({ ...d, incorporated }))}
              >
                <SelectTrigger id="incorporated"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {incorporationOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <Checkbox
                checked={draft.tribalMember}
                disabled={isSaving}
                onCheckedChange={(checked) =>
                  setDraft((d) => ({ ...d, tribalMember: checked === true }))
                }
              />
              Tribal member in household
            </label>
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <Checkbox
                checked={draft.militaryMemberInHousehold}
                disabled={isSaving}
                onCheckedChange={(checked) =>
                  setDraft((d) => ({ ...d, militaryMemberInHousehold: checked === true }))
                }
              />
              Military member in household
            </label>
          </div>
        ) : (
          <FieldGrid>
            <Field label="Housing Status" value={enumLabel(housingStatusLabels, benefit.housingStatus)} />
            <Field label="Housing Type" value={enumLabel(housingTypeLabels, benefit.housingType)} />
            <Field label="Monthly Rent Payment" value={fmtMoney(benefit.housingCost)} />
            <Field label="Move-in Date (PSE)" value={energy.moveInDate} />
            <Field
              label="Lived in Residence"
              value={`${benefit.livedAtResidenceYears}y ${benefit.livedAtResidenceMonths}m`}
            />
            {energy.bedrooms.map(([label, value]) => <Field key={label} label={label} value={value} />)}
            <Field label="Family Composition" value={enumLabel(familyTypeLabels, benefit.familyType)} />
            {energy.education.map(([label, value]) => <Field key={label} label={label} value={value} />)}
            <Field label="Tribal Member" value={boolLabel(benefit.tribalMember)} />
            <Field label="Military Member" value={boolLabel(benefit.militaryMemberInHousehold)} />
            {energy.heat.map(([label, value]) => <Field key={label} label={label} value={value} />)}
            <Field
              label="Incorporated"
              value={enumLabel(incorporationLabels, incorporationByte(benefit.incorporated))}
            />
          </FieldGrid>
        )}
      </CardContent>
    </Card>
  )
}
